"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";

import { useViewer } from "@/hooks/use-viewer";
import { setCurrencyPreference } from "@/i18n/currency-actions";
import { authClient } from "@/lib/auth-client";
import { invalidateViewerContext, patchViewerContextQueryData } from "@/lib/viewer-context";
import { normalizeViewerCurrency } from "@garimpa/lib/viewerPreferences";

export function useCurrency() {
  const queryClient = useQueryClient();
  const viewer = useViewer();
  const currency = viewer.preferences.currency;
  const [isPending, setIsPending] = useState(false);

  const setCurrency = useCallback(
    (code: string) => {
      const normalized = normalizeViewerCurrency(code);
      if (!normalized) return;

      setIsPending(true);
      patchViewerContextQueryData(queryClient, {
        user: { currency: normalized },
        preferences: { currency: normalized },
      });
      void (async () => {
        try {
          await setCurrencyPreference({ data: normalized });

          if (viewer.isAuthenticated) {
            await authClient.updateUser({ currency: normalized });
          }
        } catch (error) {
          console.error("Failed to persist currency preference", error);
        } finally {
          await invalidateViewerContext(queryClient);
          setIsPending(false);
        }
      })();
    },
    [queryClient, viewer.isAuthenticated],
  );

  return { currency, setCurrency, isPending };
}
